import { keepPreviousData, queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { PaginatedSuggestionsListQueryParams, SuggestionReviewType, SuggestionStatus } from '@/api/apis/multi-suggest';
import { MultiSuggestRoutePlanType } from '@/utils/route/chains';
import { MS_IN_S } from '@/constants/date.constants';
import { multiSuggestKeys } from './multi-suggest.keys';
import { fetchMultiSuggestItem } from './multi-suggest.fetcher';

const REFETCH_INTERVAL = 2 * MS_IN_S;

export const multiSuggestQueries = {
  list: (params: PaginatedSuggestionsListQueryParams) =>
    queryOptions({
      queryKey: multiSuggestKeys.getList(params),
      queryFn: () => api.multiSuggest.getSuggestionsList(params),
      placeholderData: keepPreviousData,
      refetchInterval: (query) => {
        const hasInProgress = query.state.data?.items?.some((s) => s.suggestionStatus === SuggestionStatus.InProgress);

        if (!hasInProgress) return false;
        return REFETCH_INTERVAL;
      },
    }),
  item: (id?: string) =>
    queryOptions({
      queryKey: multiSuggestKeys.getMultiSuggestResultItem(id),
      queryFn: id ? () => fetchMultiSuggestItem(id) : skipToken,
      staleTime: Infinity,
      enabled: !!id,
      refetchOnWindowFocus: false,
      refetchInterval: (query) => {
        const isInProgress = query.state.data?.suggestionStatus === SuggestionStatus.InProgress;

        if (!isInProgress) return false;
        return REFETCH_INTERVAL;
      },
    }),
  reviewsById: (id: string) =>
    queryOptions<SuggestionReviewType[]>({
      queryKey: multiSuggestKeys.getReviews(id),
      queryFn: skipToken,
      staleTime: Infinity,
      enabled: !!id,
      refetchOnWindowFocus: false,
    }),
  candidates: () =>
    queryOptions({
      queryKey: multiSuggestKeys.getCandidates(),
      queryFn: () => api.multiSuggest.getSuggestionsCandidates(),
    }),
  itemByChain: (taskId: string, chainIndex: number, caseId: string) =>
    queryOptions<MultiSuggestRoutePlanType | null>({
      queryKey: multiSuggestKeys.getMultiSuggestCaseResultByCase(taskId, chainIndex, caseId),
      queryFn: skipToken,
      refetchOnWindowFocus: false,
    }),
};

export const useMultiSuggestionListQuery = (params: PaginatedSuggestionsListQueryParams) => {
  return useQuery(multiSuggestQueries.list(params));
};

export const useMultiSuggestionItemQuery = (id?: string) => {
  return useQuery(multiSuggestQueries.item(id));
};

export const useMultiSuggestionItemByChainQuery = (taskId: string, chainIndex: number, caseId: string) => {
  return useQuery(multiSuggestQueries.itemByChain(taskId, chainIndex, caseId));
};

export const useMultiSuggestionItemReviewsQuery = (id: string) => {
  return useQuery(multiSuggestQueries.reviewsById(id));
};

export const useMultiSuggestionCandidatesQuery = () => {
  return useQuery(multiSuggestQueries.candidates());
};
